import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  X,
  Building2,
  Mail,
  Phone,
  MapPin,
  Download,
  FileText,
  Send,
  PenLine,
  KeyRound,
  Copy,
  Sparkles,
  Trash2,
} from "lucide-react";
import api, { adminReq } from "../../lib/api";
import { toast } from "sonner";
import SendAgreementModal from "./SendAgreementModal";
import ActivateModal from "./ActivateModal";
import useSelection from "../../hooks/useSelection";
import SelectionBar from "../../components/SelectionBar";
import CheckboxCell from "../../components/CheckboxCell";
import { ConfirmModal } from "./Trash";

const STATUS_META = {
  pending: {
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    icon: Clock,
    label: "Pending Review",
  },
  agreement_sent: {
    cls: "bg-sky-50 text-sky-700 border-sky-200",
    icon: Send,
    label: "Agreement Sent",
  },
  agreement_signed: {
    cls: "bg-indigo-50 text-indigo-700 border-indigo-200",
    icon: PenLine,
    label: "Agreement Signed",
  },
  active: {
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
    label: "Active",
  },
  rejected: {
    cls: "bg-red-50 text-red-700 border-red-200",
    icon: XCircle,
    label: "Rejected",
  },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${m.cls}`}
    >
      <Icon className="w-3 h-3" />
      {m.label}
    </span>
  );
}

export default function AdminRecruiters({ rows, onStatus, onDownload, onRefresh }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [sendAgreementFor, setSendAgreementFor] = useState(null);
  const [activated, setActivated] = useState(null);

  const filtered = useMemo(
    () =>
      (rows || []).filter((r) => {
        const matchQ =
          !q ||
          [r.full_name, r.email, r.company_name, r.country, r.city]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q.toLowerCase()));
        const matchS = statusFilter === "all" || r.status === statusFilter;
        return matchQ && matchS;
      }),
    [rows, q, statusFilter]
  );

  const sel = useSelection(filtered);
  const [confirm, setConfirm] = useState(null);

  const bulkDelete = async (ids) => {
    try {
      await api.post(
        "/admin/recruiters/bulk-delete",
        { ids },
        adminReq()
      );
      toast.success(`Moved ${ids.length} to trash`);
      sel.clear();
      if (onRefresh) onRefresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const activate = async (r) => {
    try {
      const { data } = await api.post(
        `/admin/recruiters/${r.id}/activate`,
        {},
        adminReq()
      );
      setActivated({ recruiter: r, setupUrl: data.setup_url });
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to activate");
    }
  };

  const copyLink = (path) => {
    navigator.clipboard.writeText(`${window.location.origin}${path}`);
    toast.success("Link copied");
  };

  const renderActions = (r) => {
    const status = r.status || "pending";
    if (status === "pending")
      return (
        <>
          <button
            onClick={() => setSendAgreementFor(r)}
            className="inline-flex items-center gap-1 bg-cct-blue text-white text-[11px] font-semibold px-2.5 py-1 rounded-md hover:bg-[#1a3380]"
          >
            <Send className="w-3 h-3" /> Send Agreement
          </button>
          <button
            onClick={() => onStatus(r.id, "rejected")}
            className="text-red-600 hover:underline text-[11px] font-semibold"
          >
            Reject
          </button>
        </>
      );
    if (status === "agreement_sent")
      return (
        <>
          <button
            onClick={() =>
              copyLink(`/recruiter/agreement/${r.agreement?.signing_token}`)
            }
            className="inline-flex items-center gap-1 bg-white border border-slate-300 text-slate-700 text-[11px] font-semibold px-2.5 py-1 rounded-md hover:border-cct-blue hover:text-cct-blue"
          >
            <Copy className="w-3 h-3" /> Copy Link
          </button>
          <button
            onClick={() => setSendAgreementFor(r)}
            className="text-slate-500 hover:text-cct-blue text-[11px] font-semibold"
          >
            Resend
          </button>
        </>
      );
    if (status === "agreement_signed")
      return (
        <button
          onClick={() => activate(r)}
          className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md hover:bg-emerald-700"
        >
          <KeyRound className="w-3 h-3" /> Activate
        </button>
      );
    if (status === "active")
      return (
        <span className="inline-flex items-center gap-1 text-emerald-600 text-[11px] font-semibold">
          <Sparkles className="w-3 h-3" /> Portal Active
        </span>
      );
    if (status === "rejected")
      return (
        <button
          onClick={() => onStatus(r.id, "pending")}
          className="text-slate-500 hover:text-cct-blue text-[11px] font-semibold"
        >
          Reset
        </button>
      );
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 flex flex-col sm:flex-row gap-3">
        <label className="flex items-center gap-2 px-2">
          <CheckboxCell
            checked={sel.allSelected}
            indeterminate={sel.someSelected}
            onChange={sel.toggleAll}
            ariaLabel="Select all"
          />
          <span className="text-xs text-slate-500">
            {sel.count > 0 ? `${sel.count} selected` : "Select all"}
          </span>
        </label>
        <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search recruiters..."
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
        >
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="agreement_sent">Agreement Sent</option>
          <option value="agreement_signed">Agreement Signed</option>
          <option value="active">Active</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400 text-sm">
            No recruiters found
          </div>
        ) : (
          filtered.map((r, idx) => {
            const checked = sel.isSelected(r.id);
            return (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              whileHover={{ y: -3 }}
              className={`relative bg-white border rounded-2xl shadow-sm p-5 hover:shadow-md transition-all ${
                checked
                  ? "border-amber-300 ring-2 ring-amber-200/60"
                  : "border-slate-200/80"
              }`}
            >
              <div className="absolute top-3 right-3 flex items-center gap-2">
                <button
                  onClick={() => setConfirm({ ids: [r.id] })}
                  className="text-slate-300 hover:text-red-500 p-1"
                  title="Move to trash"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <CheckboxCell
                  checked={checked}
                  onChange={() => sel.toggle(r.id)}
                  ariaLabel="Select recruiter"
                />
              </div>
              <div className="flex items-start gap-3 pr-14">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#3B5BC9] text-white flex items-center justify-center font-bold text-lg shadow-sm shrink-0">
                  {r.full_name.slice(0, 1)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-800 truncate">
                    {r.full_name}
                  </div>
                  <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {r.company_name}
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-slate-400" />
                  <span className="truncate">{r.email}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-slate-400" />
                  <span>
                    {r.phone_code} {r.phone}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-slate-400" />
                  <span>
                    {r.city}, {r.country}
                  </span>
                </div>
                {r.status === "agreement_signed" && r.agreement?.signed_name && (
                  <div className="mt-2 text-[11px] bg-indigo-50 text-indigo-700 rounded-md px-2 py-1">
                    Signed by{" "}
                    <span className="font-semibold">
                      {r.agreement.signed_name}
                    </span>{" "}
                    on{" "}
                    {new Date(r.agreement.signed_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                <button
                  onClick={() => setSelected(r)}
                  className="text-cct-blue text-xs font-semibold hover:underline inline-flex items-center gap-1"
                >
                  <Eye className="w-3.5 h-3.5" /> Details
                </button>
                <div className="flex gap-1.5 flex-wrap">{renderActions(r)}</div>
              </div>
            </motion.div>
            );
          })
        )}
      </div>

      {/* Details drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <motion.div
            initial={{ x: 400 }}
            animate={{ x: 0 }}
            transition={{ type: "spring", damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl"
          >
            <div className="p-6 border-b border-slate-200 flex justify-between items-start sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gold font-semibold">
                  Recruiter
                </div>
                <h3 className="font-display text-2xl font-semibold text-cct-blue mt-1">
                  {selected.full_name}
                </h3>
                <div className="mt-2">
                  <StatusBadge status={selected.status} />
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-2 text-sm">
              <div className="flex flex-wrap gap-2 mb-2">{renderActions(selected)}</div>
              {Object.entries(selected)
                .filter(
                  ([k]) =>
                    ![
                      "_id",
                      "id",
                      "documents",
                      "password_hash",
                      "status",
                      "password_token",
                      "password_token_expires",
                      "agreement",
                    ].includes(k)
                )
                .map(([k, v]) => (
                  <div
                    key={k}
                    className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100"
                  >
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      {k.replace(/_/g, " ")}
                    </div>
                    <div className="col-span-2 text-slate-800 break-words">
                      {String(v ?? "—")}
                    </div>
                  </div>
                ))}
              {selected.agreement && (
                <div className="pt-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Agreement
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1.5">
                    <div>
                      <span className="text-slate-500">Subject:</span>{" "}
                      <span className="text-slate-800 font-medium">
                        {selected.agreement.subject}
                      </span>
                    </div>
                    {selected.agreement.sent_at && (
                      <div>
                        <span className="text-slate-500">Sent:</span>{" "}
                        {new Date(selected.agreement.sent_at).toLocaleString()}
                      </div>
                    )}
                    {selected.agreement.signed_at && (
                      <div>
                        <span className="text-slate-500">Signed:</span>{" "}
                        {new Date(
                          selected.agreement.signed_at
                        ).toLocaleString()}{" "}
                        by{" "}
                        <span className="font-semibold">
                          {selected.agreement.signed_name}
                        </span>
                      </div>
                    )}
                    {selected.agreement.signing_token && (
                      <button
                        onClick={() =>
                          copyLink(
                            `/recruiter/agreement/${selected.agreement.signing_token}`
                          )
                        }
                        className="inline-flex items-center gap-1 text-cct-blue text-xs font-semibold hover:underline mt-1"
                      >
                        <Copy className="w-3 h-3" /> Copy signing link
                      </button>
                    )}
                  </div>
                </div>
              )}
              {selected.documents?.length > 0 && (
                <div className="pt-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Documents
                  </div>
                  <div className="space-y-2">
                    {selected.documents.map((d) => (
                      <div
                        key={d.file_id}
                        className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2.5"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-cct-blue shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate capitalize">
                              {d.label?.replace(/_/g, " ") || d.filename}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {d.filename}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => onDownload(d.file_id, d.filename)}
                          className="inline-flex items-center gap-1 text-cct-blue hover:underline text-xs font-semibold"
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {sendAgreementFor && (
        <SendAgreementModal
          recruiter={sendAgreementFor}
          onClose={() => setSendAgreementFor(null)}
          onDone={() => {
            setSendAgreementFor(null);
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {activated && (
        <ActivateModal
          recruiter={activated.recruiter}
          setupUrl={activated.setupUrl}
          onClose={() => {
            setActivated(null);
            if (onRefresh) onRefresh();
          }}
        />
      )}

      <SelectionBar
        count={sel.count}
        onClear={sel.clear}
        noun="recruiter"
        actions={[
          {
            label: "Move to Trash",
            icon: Trash2,
            variant: "danger",
            onClick: () => setConfirm({ ids: sel.selectedIds }),
          },
        ]}
      />

      {confirm && (
        <ConfirmModal
          title="Move to Trash?"
          message={`${confirm.ids.length} recruiter${
            confirm.ids.length !== 1 ? "s" : ""
          } will be moved to Trash. You can restore them later.`}
          confirmLabel="Move to Trash"
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            bulkDelete(confirm.ids);
            setConfirm(null);
          }}
        />
      )}
    </div>
  );
}
